<template>
  <Admin style="background: #f2f2f2">
    <v-main>
      <v-container fluid>
        <!-- Snackbar for notifications -->
        <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000" top>
          {{ snackbar.message }}
          <template v-slot:action="{ attrs }">
            <v-btn text v-bind="attrs" @click="snackbar.show = false">ปิด</v-btn>
          </template>
        </v-snackbar>

        <!-- // Start History Dialog -->
        <v-dialog v-model="isShowHistoryDialog" max-width="1300">
          <v-card class="tw-p-4" style="height: auto">
            <div
              class="tw-pb-4 tw-my-4 tw-text-center tw-border-b tw-border-gray-500 tw-mx-4 tw-font-bold"
            >
              ข้อมูลการใช้งานรวม
            </div>

            <v-tabs v-model="historyTabIndex" class="tw-mx-4">
              <v-tab>
                <v-icon left>mdi-clipboard-text-clock</v-icon>
                Legacy
              </v-tab>
              <v-tab>
                <v-icon left>mdi-brain</v-icon>
                AI Usage
              </v-tab>
            </v-tabs>

            <v-tabs-items v-model="historyTabIndex">
              <!-- Tab 1: Legacy (ChatGptActivityLog) -->
              <v-tab-item>
                <div
                  class="tw-mx-4 tw-mt-4 tw-max-h-132 tw-flex tw-justify-between"
                >
                  <v-select
                    :menu-props="{ bottom: true, offsetY: true }"
                    v-model="selectedMonth"
                    :items="selectedChatGptActivityLog"
                    item-value="id"
                    item-text="id"
                    label="Select Month"
                  ></v-select>
                </div>
                <div
                  class="tw-mx-4 tw-mt-4 tw-overflow-y-scroll tw-max-h-132 tw-h-146"
                >
                  <v-data-table
                    class="nowrap"
                    hide-default-footer
                    :items-per-page="-1"
                    :headers="[
                      { text: 'Date', value: 'id' },
                      { text: 'PDF File', value: 'countDetails' },
                      { text: 'Input Tokens', value: 'tokenPrompt' },
                      { text: 'Output Tokens', value: 'tokenCompletion' },
                      { text: 'Total Tokens', value: 'tokenTotal' },
                    ]"
                    :loading="loading"
                    loading-text="Loading... Please wait"
                    :items="formatNumbers(selectedDateChatGptActivityLog)"
                    item-key="id"
                    :single-expand="singleExpand"
                    :expanded.sync="expanded"
                    show-expand
                  >
                    <template v-slot:expanded-item="{ item }">
                      <td :colspan="6" class="expanded-item-td">
                        <v-data-table
                          hide-default-footer
                          dense
                          class="elevation-1"
                          :headers="[
                            { text: 'Model Version', value: 'modelVersion', class: 'my-header-style' },
                            { text: 'PDF File', value: 'countDetails', class: 'my-header-style' },
                            { text: 'Input Tokens', value: 'tokenPrompt', class: 'my-header-style' },
                            { text: 'Output Tokens', value: 'tokenCompletion', class: 'my-header-style' },
                            { text: 'Total Tokens', value: 'tokenTotal', class: 'my-header-style' },
                          ]"
                          :items="formatNumbers(item.modelData)"
                          item-key="modelVersion"
                          full-width
                          :item-class="rowClass"
                        ></v-data-table>
                      </td>
                    </template>
                  </v-data-table>
                </div>

                <div class="tw-mx-4 tw-mt-4 tw-border-t tw-py-4">
                  <v-row
                    justify="end"
                    v-for="model in modelData"
                    :key="model.modelVersion"
                    class="pa-0 ma-0"
                  >
                    <v-col cols="12" sm="6" md="4" class="tw-py-0">
                      <v-text-field label="Model" readonly :value="model?.modelVersion" outlined class="text-right"></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6" md="2" class="tw-py-0">
                      <v-text-field label="Total PDFs" readonly :value="model.countDetails?.toLocaleString()" reverse outlined class="text-right"></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6" md="2" class="tw-py-0">
                      <v-text-field label="Total Inputs" readonly :value="model.tokenPrompt?.toLocaleString()" reverse outlined class="text-right"></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6" md="2" class="tw-py-0">
                      <v-text-field label="Total Outputs" readonly :value="model.tokenCompletion?.toLocaleString()" reverse outlined class="text-right"></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6" md="2" class="tw-py-0">
                      <v-text-field label="Net Tokens" readonly :value="model.tokenTotal?.toLocaleString()" class="text-right" outlined reverse></v-text-field>
                    </v-col>
                  </v-row>
                </div>
              </v-tab-item>

              <!-- Tab 2: AI Usage (AiUsageLog) -->
              <v-tab-item>
                <div class="tw-mx-4 tw-mt-4 tw-max-h-132 tw-flex tw-justify-between">
                  <v-select
                    :menu-props="{ bottom: true, offsetY: true }"
                    v-model="selectedAiMonth"
                    :items="selectedAiUsageLog"
                    item-value="id"
                    item-text="id"
                    label="Select Month"
                  ></v-select>
                </div>
                <div class="tw-mx-4 tw-mt-4 tw-overflow-y-scroll tw-max-h-132 tw-h-146">
                  <v-data-table
                    class="nowrap"
                    hide-default-footer
                    :items-per-page="-1"
                    :headers="[
                      { text: 'Date', value: 'id' },
                      { text: 'Requests', value: 'countDetails' },
                      { text: 'Input Tokens', value: 'tokenPrompt' },
                      { text: 'Output Tokens', value: 'tokenCompletion' },
                      { text: 'Cached', value: 'cachedTokens' },
                      { text: 'Reasoning', value: 'reasoningTokens' },
                      { text: 'Total Tokens', value: 'tokenTotal' },
                    ]"
                    :loading="loading"
                    loading-text="Loading... Please wait"
                    no-data-text="ไม่พบข้อมูลการใช้งานในเดือนนี้"
                    :items="formatAiNumbers(selectedDateAiUsageLog)"
                    item-key="id"
                    :single-expand="singleExpand"
                    :expanded.sync="aiExpanded"
                    show-expand
                  >
                    <template v-slot:expanded-item="{ item }">
                      <td :colspan="8" class="expanded-item-td">
                        <v-data-table
                          hide-default-footer
                          dense
                          class="elevation-1"
                          :headers="[
                            { text: 'Model Version', value: 'modelVersion', class: 'my-header-style' },
                            { text: 'Requests', value: 'countDetails', class: 'my-header-style' },
                            { text: 'Input Tokens', value: 'tokenPrompt', class: 'my-header-style' },
                            { text: 'Output Tokens', value: 'tokenCompletion', class: 'my-header-style' },
                            { text: 'Cached', value: 'cachedTokens', class: 'my-header-style' },
                            { text: 'Reasoning', value: 'reasoningTokens', class: 'my-header-style' },
                            { text: 'Total Tokens', value: 'tokenTotal', class: 'my-header-style' },
                          ]"
                          :items="formatAiNumbers(toEntries(item.modelTotals, 'modelVersion'))"
                          item-key="modelVersion"
                          full-width
                          :item-class="rowClass"
                        ></v-data-table>
                      </td>
                    </template>
                  </v-data-table>
                </div>

                <div v-if="aiModelTotalsList.length" class="tw-mx-4 tw-mt-4 tw-border-t tw-py-4">
                  <div class="tw-font-bold tw-mb-2">สรุปยอดรวมตามโมเดล</div>
                  <v-row
                    justify="end"
                    v-for="model in aiModelTotalsList"
                    :key="'m-' + model.modelVersion"
                    class="pa-0 ma-0"
                  >
                    <v-col cols="12" sm="6" md="3" class="tw-py-0">
                      <v-text-field label="Model" readonly :value="model.modelVersion" outlined></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6" md="1" class="tw-py-0">
                      <v-text-field label="Requests" readonly :value="model.countDetails?.toLocaleString()" reverse outlined></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6" md="2" class="tw-py-0">
                      <v-text-field label="Input" readonly :value="model.tokenPrompt?.toLocaleString()" reverse outlined></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6" md="2" class="tw-py-0">
                      <v-text-field label="Output" readonly :value="model.tokenCompletion?.toLocaleString()" reverse outlined></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6" md="2" class="tw-py-0">
                      <v-text-field label="Cached" readonly :value="model.cachedTokens?.toLocaleString()" reverse outlined></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6" md="2" class="tw-py-0">
                      <v-text-field label="Total" readonly :value="model.tokenTotal?.toLocaleString()" reverse outlined></v-text-field>
                    </v-col>
                  </v-row>
                </div>

                <div v-if="aiServiceTotalsList.length" class="tw-mx-4 tw-mt-4 tw-border-t tw-py-4">
                  <div class="tw-font-bold tw-mb-2">สรุปยอดรวมตามบริการ</div>
                  <v-row
                    justify="end"
                    v-for="svc in aiServiceTotalsList"
                    :key="'s-' + svc.serviceType"
                    class="pa-0 ma-0"
                  >
                    <v-col cols="12" sm="6" md="3" class="tw-py-0">
                      <v-text-field label="Service" readonly :value="svc.serviceType" outlined></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6" md="1" class="tw-py-0">
                      <v-text-field label="Requests" readonly :value="svc.countDetails?.toLocaleString()" reverse outlined></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6" md="2" class="tw-py-0">
                      <v-text-field label="Input" readonly :value="svc.tokenPrompt?.toLocaleString()" reverse outlined></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6" md="2" class="tw-py-0">
                      <v-text-field label="Output" readonly :value="svc.tokenCompletion?.toLocaleString()" reverse outlined></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6" md="2" class="tw-py-0">
                      <v-text-field label="Cached" readonly :value="svc.cachedTokens?.toLocaleString()" reverse outlined></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6" md="2" class="tw-py-0">
                      <v-text-field label="Total" readonly :value="svc.tokenTotal?.toLocaleString()" reverse outlined></v-text-field>
                    </v-col>
                  </v-row>
                </div>
              </v-tab-item>
            </v-tabs-items>

            <div class="tw-py-4 tw-mx-4 tw-flex tw-mt-2 tw-border-gray-500">
              <v-btn outlined @click="isShowHistoryDialog = false">ปิด</v-btn>
            </div>
          </v-card>
        </v-dialog>

        <!-- // End of History Dialog -->

        <v-card class="rounded-xl tw-min-h-80">
          <v-card-text>
            <!-- Breadcrumbs -->
            <v-breadcrumbs :items="items">
              <template v-slot:divider>
                <v-icon>mdi-chevron-right</v-icon>
              </template>
              <template v-slot:item="{ item }">
                <v-breadcrumbs-item :to="item.to" :disabled="item.disabled" :exact="true">
                  {{ item.text }}
                </v-breadcrumbs-item>
              </template>
            </v-breadcrumbs>

            <!-- API Key Section -->
            <div v-if="isStaff" class="tw-mx-5 tw-font-bold tw-text-lg tw-text-black tw-tracking-wide">
              <div class="tw-flex tw-justify-between tw-items-center">
                <span class="tw-font-bold tw-text-lg tw-text-black tw-tracking-wide"> ตั้งค่าการเชื่อมต่อ API</span>
                <div class="tw-flex tw-gap-4">
                  <v-btn @click="showHistoryDialog" text outlined color="brown" :disabled="isInitializing">
                    <v-icon left>mdi-history</v-icon>
                    ข้อมูลการใช้งานรวม
                  </v-btn>
                </div>
              </div>
            </div>
            <div v-if="isStaff" class="tw-flex tw-mx-8 tw-mt-8">
              <v-row class="tw-flex tw-gap-4">
                <v-col cols="4" class="ma-0 pa-0">
                  <v-text-field
                    v-model="apiKey"
                    outlined
                    dense
                    :type="showKey ? 'text' : 'password'"
                    :append-icon="showKey ? 'mdi-eye' : 'mdi-eye-off'"
                    @click:append="toggleShowKey"
                    label="API Key"
                    persistent-placeholder
                  ></v-text-field>
                </v-col>
                <v-col cols="4" class="ma-0 pa-0">
                  <v-btn color="primary" :loading="isSavingApiKey" :disabled="!apiKey" @click="saveApiKey" class="tw-mr-4">
                    <v-icon left>mdi-content-save</v-icon>
                    <span class="ml-2">บันทึก</span>
                  </v-btn>
                  <v-btn color="info" :loading="isTestingConnection" :disabled="!apiKey" outlined @click="testConnection">
                    <v-icon left>mdi-router-wireless</v-icon>
                    <span class="ml-2">ทดสอบ</span>
                  </v-btn>
                </v-col>
              </v-row>
            </div>

            <!-- Tabs -->
            <div class="tw-mx-5 tw-mt-8">
              <v-tabs v-model="currentTabIndex">
                <v-tab>
                  <v-icon left>mdi-account-group</v-icon>
                  ผู้จำหน่าย
                </v-tab>
                <v-tab>
                  <v-icon left>mdi-brain</v-icon>
                  VendorIQ
                </v-tab>
              </v-tabs>
            </div>

            <v-tabs-items v-model="currentTabIndex">
              <!-- Tab 1: ผู้จำหน่าย -->
              <v-tab-item>
            <!-- Vendor Management Section -->
            <div class="tw-mx-5 tw-mt-8">
              <div class="tw-flex tw-justify-between tw-items-center">
                <span class="tw-font-bold tw-text-lg tw-text-black tw-tracking-wide">ผู้จำหน่าย</span>
                <div class="tw-flex tw-gap-4">
                  <v-btn outlined color="primary" :disabled="isInitializing" @click="addNewVendor">
                    <v-icon left>mdi-plus</v-icon>
                    เพิ่มผู้จำหน่าย
                  </v-btn>
                  <!-- Sync Vendor from FoxPro -->
                  <v-btn
                    v-if="isApTypeTwo"
                    outlined
                    color="indigo"
                    :loading="isSyncingVendors"
                    :disabled="isInitializing || isSyncingVendors"
                    @click="confirmSyncVendors"
                  >
                    <v-icon left>mdi-database-sync</v-icon>
                    Sync Vendor
                  </v-btn>
                  <!-- Update Expense Button -->
                  <v-btn outlined color="teal" :disabled="isInitializing" @click="openUpdateExpenseDialog">
                    <v-icon left>mdi-cash-refresh</v-icon>
                    Update Expense
                  </v-btn>
                  <!-- CSV Template Dialog Button -->
                  <v-btn outlined color="secondary" :disabled="isInitializing" @click="openCsvTemplateDialog">
                    <v-icon left>mdi-file-document-outline</v-icon>
                    CSV Template
                  </v-btn>
                  <!-- Bulk Delete Button -->
                  <v-btn outlined color="error" :disabled="selectedVendorIds.length === 0 || isInitializing" @click="confirmBulkDelete">
                    <v-icon left>mdi-delete</v-icon>
                    Bulk Delete
                  </v-btn>
                  <v-btn color="success" :loading="savingChanges" :disabled="!hasUnsavedChanges || savingChanges" @click="saveChanges">
                    <v-icon left>mdi-content-save</v-icon>
                    บันทึกข้อมูลผู้จำหน่าย
                  </v-btn>
                </div>
              </div>
            </div>

            <!-- Vendor Search and Table -->
            <div>
              <div class="tw-flex tw-items-center tw-mt-8 tw-pr-5">
                <v-text-field
                  v-model="vendorSearch"
                  dense
                  outlined
                  class="tw-w-3 tw-mx-5 tw-mt-2"
                  placeholder="ค้นหาผู้จำหน่าย"
                  prepend-inner-icon="mdi-magnify"
                  hide-details
                ></v-text-field>
                <v-spacer></v-spacer>
                <v-spacer></v-spacer>
              </div>
              <div class="tw-mx-5 tw-py-5 tw-overflow-y-auto">
                <!-- Refresh indicator: shown during in-place refresh (sync), keeps existing rows visible -->
                <v-progress-linear
                  indeterminate
                  color="primary"
                  :active="isRefreshing"
                  class="tw-mb-2"
                ></v-progress-linear>

                <!-- Initial-load skeleton: replaces the table on first load after a 200ms grace window -->
                <div
                  v-if="showInitialSkeleton"
                  class="vendor-skeleton"
                  aria-busy="true"
                  aria-live="polite"
                >
                  <div class="vendor-skeleton__inner">
                    <div class="vendor-skeleton__header">
                      <div class="vendor-skeleton__cell" style="width: 50px"></div>
                      <div class="vendor-skeleton__cell" style="width: 60px">ลำดับ</div>
                      <div class="vendor-skeleton__cell" style="width: 200px">venTaxId</div>
                      <div class="vendor-skeleton__cell" style="width: 90px">ละเว้น</div>
                      <div class="vendor-skeleton__cell" style="width: 90px">รวมภาษี</div>
                      <div class="vendor-skeleton__cell" style="width: 120px">ต่างประเทศ</div>
                      <div class="vendor-skeleton__cell" style="width: 110px">รหัสบริษัท</div>
                      <div class="vendor-skeleton__cell" style="width: 150px">คำนำหน้า</div>
                      <div class="vendor-skeleton__cell" style="width: 250px">ชื่อบริษัท</div>
                      <div class="vendor-skeleton__cell" style="width: 120px">รหัสสาขา</div>
                      <div class="vendor-skeleton__cell vendor-skeleton__cell--flex">ที่อยู่</div>
                      <div class="vendor-skeleton__cell" style="width: 120px">Actions</div>
                    </div>
                    <div
                      v-for="n in skeletonRowCount"
                      :key="n"
                      class="vendor-skeleton__row"
                    >
                      <div class="vendor-skeleton__cell" style="width: 50px">
                        <div class="skel-bar skel-bar--checkbox"></div>
                      </div>
                      <div class="vendor-skeleton__cell" style="width: 60px">
                        <div class="skel-bar" style="width: 24px"></div>
                      </div>
                      <div class="vendor-skeleton__cell" style="width: 200px">
                        <div class="skel-bar" style="width: 70%"></div>
                      </div>
                      <div class="vendor-skeleton__cell" style="width: 90px">
                        <div class="skel-bar" style="width: 50%"></div>
                      </div>
                      <div class="vendor-skeleton__cell" style="width: 90px">
                        <div class="skel-bar" style="width: 50%"></div>
                      </div>
                      <div class="vendor-skeleton__cell" style="width: 120px">
                        <div class="skel-bar" style="width: 60%"></div>
                      </div>
                      <div class="vendor-skeleton__cell" style="width: 110px">
                        <div class="skel-bar" style="width: 70%"></div>
                      </div>
                      <div class="vendor-skeleton__cell" style="width: 150px">
                        <div class="skel-bar" style="width: 60%"></div>
                      </div>
                      <div class="vendor-skeleton__cell" style="width: 250px">
                        <div class="skel-bar" style="width: 85%"></div>
                      </div>
                      <div class="vendor-skeleton__cell" style="width: 120px">
                        <div class="skel-bar" style="width: 60%"></div>
                      </div>
                      <div class="vendor-skeleton__cell vendor-skeleton__cell--flex">
                        <div class="skel-bar" style="width: 90%"></div>
                      </div>
                      <div class="vendor-skeleton__cell" style="width: 120px">
                        <div class="skel-bar skel-bar--circle"></div>
                        <div class="skel-bar skel-bar--circle"></div>
                      </div>
                    </div>
                  </div>
                </div>

                <el-table
                  v-else-if="!isInitialLoading"
                  :data="paginatedVendors"
                  style="width: 100%"
                  empty-text="ไม่พบผู้จำหน่าย"
                  @selection-change="handleSelectionChange"
                  :default-sort="{prop: 'venCode', order: 'ascending'}"
                >
                  <el-table-column
                    type="selection"
                    width="50"
                    fixed="left"
                  >
                  </el-table-column>
                  <el-table-column
                    prop="index"
                    label="ลำดับ"
                    width="60"
                    align="center"
                    fixed="left"
                  >
                    <template slot-scope="scope">
                      {{ (vendorPage - 1) * vendorPageSize + scope.$index + 1 }}
                    </template>
                  </el-table-column>

                  <el-table-column
                    prop="venTaxId"
                    label="venTaxId"
                    width="200"
                    align="center"
                    fixed="left"
                  >
                    <template slot-scope="scope">
                      {{ scope.row.venTaxId }}
                    </template>
                  </el-table-column>

                  <el-table-column
                    prop="isIncludeVat"
                    label="ละเว้น"
                    width="90"
                    align="center"
                    fixed="left"
                    sortable
                  >
                    <template slot-scope="scope">
                      <span  v-if="scope.row.isRejected"> <v-icon>mdi-sign-caution</v-icon> ละเว้น</span>
                    </template>
                  </el-table-column>
                  <el-table-column
                    prop="isIncludeVat"
                    label="รวมภาษี"
                    width="90"
                    align="center"
                    fixed="left"
                    sortable
                  >
                    <template slot-scope="scope">
                      <span  v-if="scope.row.isIncludeVat"> <v-icon>mdi-approximately-equal-box</v-icon> รวม</span>
                    </template>
                  </el-table-column>
                  <el-table-column
                    prop="isForeign"
                    label="ต่างประเทศ"
                    width="120"
                    align="center"
                    fixed="left"
                    sortable
                  >
                    <template slot-scope="scope">
                      <span v-if="scope.row.isForeign"><v-icon>mdi-earth</v-icon> ต่างประเทศ</span>
                    </template>
                  </el-table-column>
                  <el-table-column
                    prop="venCode"
                    label="รหัสบริษัท"
                    width="110"
                    fixed="left"
                    sortable
                  >
                  </el-table-column>
                  <el-table-column
                    prop="venPrefix"
                    label="คำนำหน้า"
                    width="150"
                  >
                  </el-table-column>
                  <el-table-column
                    prop="venName"
                    label="ชื่อบริษัท"
                    width="250"
                    sortable
                  >
                  </el-table-column>
                  <el-table-column
                    prop="venBranch"
                    label="รหัสสาขา"
                    width="120"
                  >
                  </el-table-column>
                  <el-table-column
                    prop="venAddr"
                    label="ที่อยู่"
                    min-width="300"
                  >
                  </el-table-column>
                  <el-table-column
                    label="Actions"
                    width="120"
                    align="center"
                    fixed="right"
                  >
                    <template slot-scope="scope">
                     <div class="tw-flex tw-justify-center tw-items-center">
                      <el-button
                        type="warning"
                        icon="el-icon-edit"
                        circle
                        @click="editVendor(scope.row)"
                        style="margin-right: 4px;"
                      ></el-button>
                      <el-button
                        type="danger"
                        icon="el-icon-delete"
                        circle
                        @click="confirmVendorDelete(scope.row)"
                      ></el-button>
                     </div>
                    </template>
                  </el-table-column>
                </el-table>
              </div>
              <v-row justify="start" align="center" class="tw-my-4">
                <div class="tw-px-4">
                  {{ vendorItemsRangeText }}
                </div>
                <v-spacer />
                <v-pagination
                  v-model="vendorPage"
                  :length="vendorNumPages"
                  :total-visible="6"
                ></v-pagination>
                <v-spacer />
                <div class="tw-px-4">
                  <select class="items-per-page-selector" v-model.number="vendorPageSize">
                    <option v-for="opt in vendorPageSizeOptions" :key="opt" :value="opt">
                      {{ opt }} / หน้า
                    </option>
                  </select>
                </div>
              </v-row>
            </div>
              </v-tab-item>

              <!-- Tab 2: VendorIQ -->
              <v-tab-item>
                <ap-vendoriq :company-id="$route.params.companyId" />
              </v-tab-item>
            </v-tabs-items>
          </v-card-text>
        </v-card>

        <!-- Vendor Form Dialog -->
        <ap-vendor-form-dialog
          :is-open="showApVendorFormDialog"
          :is-edit-mode="isEditMode"
          :ap-vendor="apVendor"
          :companyID="$route.params.companyId"
          :expenseList="expenseList"
          :paymentList="paymentList"
          :vatList="vatList"
          :whtList="whtList"
          @update:isOpen="showApVendorFormDialog = $event"
          @vendor-updated="updateVendor"
        />

        <!-- Single Vendor Delete Confirmation Dialog -->
        <confirm-delete-vendor-dialog
          :is-open="showVendorDeleteDialog"
          :doc-id-label="vendorToDelete?.venName"
          @confirm="deleteVendor"
          @update:isOpen="showVendorDeleteDialog = $event"
        />

        <!-- Sync Vendor from FoxPro Confirmation Dialog -->
        <v-dialog v-model="showSyncVendorsDialog" persistent max-width="560px">
          <v-card>
            <v-card-title class="headline">Sync Vendor จาก FoxPro?</v-card-title>
            <v-card-text>
              <div class="tw-mb-3 tw-p-2 tw-rounded tw-bg-gray-100 tw-text-sm">
                <strong>ซิงก์ล่าสุด:</strong> {{ foxproLastSyncText }}
              </div>
              <p>ระบบจะดึงข้อมูลผู้จำหน่ายจากตาราง <strong>APMAS</strong> มาบันทึกลงในระบบ</p>
              <p class="tw-mb-2"><strong>ข้อมูลที่จะถูกอัปเดต</strong> (ตรงกันด้วยรหัสบริษัท / SUPCOD):</p>
              <ul class="tw-mb-3 tw-pl-4">
                <li>รหัสบริษัท, คำนำหน้า, ชื่อ, ที่อยู่, รหัสสาขา, เลขผู้เสียภาษี, รหัสไปรษณีย์</li>
              </ul>
              <p class="tw-mb-2"><strong>ข้อมูลที่จะ <em>ไม่</em> ถูกแตะ</strong>:</p>
              <ul class="tw-pl-4">
                <li>การตั้งค่าบัญชี (P00 / P07), การชำระเงิน, ละเว้น/รวมภาษี/ต่างประเทศ</li>
              </ul>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="blue darken-1" text :disabled="isSyncingVendors" @click="showSyncVendorsDialog = false">ยกเลิก</v-btn>
              <v-btn color="indigo" text :loading="isSyncingVendors" @click="syncVendorsFromFoxpro">เริ่ม Sync</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- Update Expense Dialog: bulk-update Expense (P00/P07) and WHT codes via CSV -->
        <v-dialog v-model="showUpdateExpenseDialog" persistent max-width="900px">
          <v-card>
            <v-card-title>
              <span class="headline">Update Expense</span>
            </v-card-title>
            <v-card-text>
              <v-container>
                <v-row>
                  <v-col cols="12">
                    <h3 class="tw-mb-2">1. ดาวน์โหลด CSV Template</h3>
                    <p class="tw-text-sm tw-text-gray-600 tw-mb-3">
                      คอลัมน์: <strong>VendorCode, Expense Code P07, Expense Code P00, whtcode</strong>
                    </p>
                    <v-btn color="primary" outlined @click="downloadUpdateExpenseTemplate">
                      <v-icon left>mdi-download</v-icon>
                      ดาวน์โหลด
                    </v-btn>
                  </v-col>

                  <v-col cols="12" class="mt-4">
                    <h3 class="tw-mb-2">2. เลือกไฟล์ที่กรอกข้อมูลแล้ว</h3>
                    <p class="tw-text-sm tw-text-gray-600 tw-mb-3">
                      รองรับเฉพาะไฟล์ที่ใช้ template
                      <strong>update_expense_template.csv</strong>
                    </p>
                    <v-file-input
                      v-model="updateExpenseFile"
                      label="เลือกไฟล์ CSV"
                      accept=".csv"
                      outlined
                      dense
                      @change="onUpdateExpenseFileChange"
                    ></v-file-input>
                    <v-btn
                      color="primary"
                      :loading="isUploadingUpdateExpense"
                      :disabled="!updateExpenseFile || isApplyingUpdateExpense"
                      @click="previewUpdateExpenseFile"
                    >
                      <v-icon left>mdi-file-eye</v-icon>
                      อ่านข้อมูล
                    </v-btn>
                  </v-col>

                  <v-col cols="12" v-if="updateExpenseRows.length" class="mt-4">
                    <h3 class="tw-mb-2">
                      3. ตรวจสอบข้อมูลก่อนยืนยัน ({{ updateExpenseRows.length }} แถว)
                    </h3>
                    <v-data-table
                      dense
                      :headers="updateExpenseHeaders"
                      :items="updateExpenseRows"
                      :items-per-page="10"
                      :footer-props="{ 'items-per-page-options': [10, 25, 50, 100, -1] }"
                      class="tw-border tw-rounded"
                    ></v-data-table>
                  </v-col>
                </v-row>
              </v-container>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                color="blue darken-1"
                text
                :disabled="isApplyingUpdateExpense"
                @click="closeUpdateExpenseDialog"
              >ปิด</v-btn>
              <v-btn
                color="success"
                :loading="isApplyingUpdateExpense"
                :disabled="!updateExpenseRows.length || isApplyingUpdateExpense"
                @click="applyUpdateExpense"
              >
                <v-icon left>mdi-check</v-icon>
                ยืนยันการอัปเดต
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- Bulk Delete Confirmation Dialog -->
        <v-dialog v-model="showBulkDeleteDialog" persistent max-width="500px">
          <v-card>
            <v-card-title class="headline">ยืนยันการลบ</v-card-title>
            <v-card-text>
              คุณต้องการลบผู้จำหน่ายที่เลือกทั้งหมดหรือไม่?
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="blue darken-1" text @click="showBulkDeleteDialog = false">ยกเลิก</v-btn>
              <v-btn color="red darken-1" text @click="bulkDelete">ยืนยัน</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>


        <!-- CSV Template Dialog for Download & Upload -->
        <v-dialog v-model="showCsvTemplateDialog" persistent max-width="600px">
          <v-card>
            <v-card-title>
              <span class="headline">CSV Template</span>
            </v-card-title>
            <v-card-text>
              <v-container>
                <v-row>
                  <v-col cols="12">
                    <h3 class="tw-mb-2">ดาวน์โหลด CSV Template</h3>
                    <v-btn color="primary" @click="downloadVendorTemplate">
                      <v-icon left>mdi-download</v-icon>
                      ดาวน์โหลด
                    </v-btn>
                  </v-col>
                  <v-col cols="12" class="mt-4">
                    <h3 class="tw-mb-2">อัปโหลด CSV Template</h3>
                    <v-file-input
                      v-model="selectedFile"
                      label="เลือกไฟล์ CSV"
                      accept=".csv"
                      outlined
                      dense
                    ></v-file-input>
                    <v-btn color="primary" :loading="uploadLoading" :disabled="!selectedFile" @click="confirmUpload">
                      <v-icon left>mdi-upload</v-icon>
                      อัปโหลด
                    </v-btn>
                  </v-col>
                </v-row>
              </v-container>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="blue darken-1" text @click="closeCsvTemplateDialog">ปิด</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

      </v-container>
    </v-main>
  </Admin>
</template>

<script>
import Admin from "../../components/admin/admin-main-template";
import { mapState } from "vuex";
import fc from "@/firebaseConfig";
import firebase from "firebase/app";
import ApVendorFormDialog from "@/components/admin/automation/ap-vendor-form-dialog.vue";
import confirmDeleteVendorDialog from "@/components/admin/automation/confirm-delete-vendor-dialog.vue";
import ApVendoriq from "@/components/admin/automation/ap-vendoriq.vue";
import Papa from "papaparse";
import axios from "axios";

export default {
  name: "AI",
  components: { Admin, ApVendorFormDialog, confirmDeleteVendorDialog, ApVendoriq },
  data: () => ({
    selectedMonth: "",
    expanded: [],
    modelData: [],
    singleExpand: true,
    historyTabIndex: 0,
    selectedAiMonth: "",
    aiExpanded: [],
    savingChanges: false,
    snackbar: { show: false, message: "", color: "success" },
    items: [
      { text: "ตั้งค่า", disabled: false, to: { name: "Company.Settings" } },
      { text: "ตั้งค่าผู้จำหน่าย", disabled: true },
    ],
    currentTabIndex: 0,
    showKey: false,
    vendorSearch: "",
    isTestingConnection: false,
    isSavingApiKey: false,
    showDeleteDialog: false,
    hasUnsavedChanges: false,
    apiKey: "",
    isInitializing: true,
    showApVendorFormDialog: false,
    isEditMode: false,
    apVendor: {},
    expenseList: [],
    vatList: [],
    whtList: [],
    paymentList: [],
    companyID: null,
    showVendorDeleteDialog: false,
    vendorToDelete: null,
    // For CSV Template dialog and local vendor editing:
    showCsvTemplateDialog: false,
    selectedFile: null,
    uploadLoading: false,
    editedVendors: [], // Local copy for editing vendors (not yet saved to Firebase)
    vendorsToDelete: [], // Track vendor IDs marked for deletion
    // For bulk delete via checkboxes:
    selectedVendorIds: [],
    showBulkDeleteDialog: false,
    isShowHistoryDialog: false,
    isSyncingVendors: false,
    showSyncVendorsDialog: false,
    syncVendorsResult: null,
    showSkeletonAfterDelay: false,
    showUpdateExpenseDialog: false,
    updateExpenseFile: null,
    isUploadingUpdateExpense: false,
    isApplyingUpdateExpense: false,
    updateExpenseRows: [],
    foxproLastSyncedAt: null,
    foxproLastSyncedBy: "",
    vendorPage: 1,
    vendorPageSize:
      localStorage.getItem("pageLimit") &&
      localStorage.getItem("pageLimit") === "auto"
        ? 10
        : Number.parseInt(localStorage.getItem("pageLimit")) || 10,
    vendorPageSizeOptions: [10, 25, 50, 100, 200],
  }),
  created() {
    this.skeletonTimer = null;
    const companyID = this.$route.params.companyId;
    if (companyID) {
      this.$store.dispatch("user/getRoleUser", { companyId: companyID });
    }


    this.getChatGptActivityLog();
    this.getAiUsageLog();
    this.selectedMonth = this.getCurrentMonth();
    this.selectedAiMonth = this.getCurrentMonth();

  },
  computed: {
    ...mapState({
      selectedCompany: (state) => state.companies.selectedCompany,
      userCompany: (state) => state.user.userCompany,
      selectedCompanyApVendors: (state) => state.companies.selectedCompanyApVendors,
      selectedCompanyProductList: (state) => state.companies.selectedCompanyProductList,
      loading: (state) => state.companies.loading,
      selectedChatGptActivityLog: (state) =>
        state.companies.selectedChatGptActivityLog,
      selectedDateChatGptActivityLog: (state) =>
        state.companies.selectedDateChatGptActivityLog,
      selectedDateDetailChatGptActivityLog: (state) =>
        state.companies.selectedDateDetailChatGptActivityLog,
      selectedAiUsageLog: (state) => state.companies.selectedAiUsageLog,
      selectedDateAiUsageLog: (state) => state.companies.selectedDateAiUsageLog,
    }),
    aiSelectedMonthDoc() {
      return this.selectedAiUsageLog.find((m) => m.id === this.selectedAiMonth) || null;
    },
    aiModelTotalsList() {
      const doc = this.aiSelectedMonthDoc;
      return this.toEntries(doc && doc.modelTotals, "modelVersion");
    },
    aiServiceTotalsList() {
      const doc = this.aiSelectedMonthDoc;
      return this.toEntries(doc && doc.serviceTotals, "serviceType");
    },
    isStaff() {
      return this.userCompany?.user?.isEngineer || this.userCompany?.isDeveloper;
    },
    isApTypeTwo() {
      return this.selectedCompanyProductList?.isApAutomateType === 2;
    },
    isCompanyAdmin() {
      return this.userCompany?.isAdmin;
    },
    filteredVendors() {
      const filtered = this.editedVendors.filter((vendor) =>
        vendor.venName?.toLowerCase().includes(this.vendorSearch?.toLowerCase()) ||
        vendor.venCode?.toLowerCase().includes(this.vendorSearch?.toLowerCase()) ||
        vendor.venTaxId?.toLowerCase().includes(this.vendorSearch?.toLowerCase())
      );
      // Stable order across pages — ascending by venCode (matches :default-sort).
      return filtered.slice().sort((a, b) =>
        String(a.venCode || "").localeCompare(String(b.venCode || ""))
      );
    },
    paginatedVendors() {
      const start = (this.vendorPage - 1) * this.vendorPageSize;
      return this.filteredVendors.slice(start, start + this.vendorPageSize);
    },
    vendorNumPages() {
      return Math.max(1, Math.ceil(this.filteredVendors.length / this.vendorPageSize));
    },
    vendorItemsRangeText() {
      const total = this.filteredVendors.length;
      if (total === 0) return "0 - 0 จาก 0";
      const start = (this.vendorPage - 1) * this.vendorPageSize + 1;
      const end = Math.min(this.vendorPage * this.vendorPageSize, total);
      return `${start} - ${end} จาก ${total}`;
    },
    isInitialLoading() {
      return this.isInitializing;
    },
    isRefreshing() {
      return this.isSyncingVendors;
    },
    showInitialSkeleton() {
      return this.isInitialLoading && this.showSkeletonAfterDelay;
    },
    skeletonRowCount() {
      return Math.min(this.vendorPageSize || 10, 10);
    },
    updateExpenseHeaders() {
      return [
        { text: "VendorCode", value: "VendorCode" },
        { text: "Expense Code P07", value: "Expense Code P07" },
        { text: "Expense Code P00", value: "Expense Code P00" },
        { text: "whtcode", value: "whtcode" },
      ];
    },
    foxproLastSyncText() {
      if (!this.foxproLastSyncedAt) return "ยังไม่เคยซิงก์";
      const raw = this.foxproLastSyncedAt;
      const date = raw && typeof raw.toDate === "function" ? raw.toDate() : new Date(raw);
      if (isNaN(date.getTime())) return "ยังไม่เคยซิงก์";
      const formatted = date.toLocaleString("th-TH", { dateStyle: "short", timeStyle: "short" });
      return this.foxproLastSyncedBy ? `${formatted} (โดย ${this.foxproLastSyncedBy})` : formatted;
    },
    allSelected() {
      const filteredIds = this.filteredVendors.map((v) => v.vendorId);
      return filteredIds.length > 0 && this.selectedVendorIds.length === filteredIds.length;
    }
  },
  watch: {
    selectedCompany: {
      handler(newVal) {
        if (newVal) {
          this.apiKey = newVal.openAiKey || "";
        }
      },
      immediate: true,
    },
    // Update local copy if store vendors change and no unsaved changes exist.
    selectedCompanyApVendors(newVal) {
      if (!this.hasUnsavedChanges) {
        this.editedVendors = JSON.parse(JSON.stringify(newVal));
      }
    },
    vendorSearch() {
      // Reset to first page when filter changes — otherwise the user can be
      // stranded on an empty page-N of a small filtered set.
      this.vendorPage = 1;
    },
    vendorNumPages(newVal) {
      // Clamp current page if rows shrank (e.g. after delete).
      if (this.vendorPage > newVal) this.vendorPage = newVal;
    },
    vendorPageSize(newVal) {
      // Persist user's preference and jump to first page so we don't show
      // a half-empty last page.
      localStorage.setItem("pageLimit", String(newVal));
      this.vendorPage = 1;
    },
    selectedMonth(newVal, oldVal) {
      console.log("Selected month changed:", newVal);
      let companyId = this.$route.params.companyId;
      this.$store.dispatch("companies/getDateListChatGptActivityLog", {
        companyId,
        newVal,
      });
      this.modelData = this.findModelData(newVal);
    },
    selectedChatGptActivityLog(newVal, oldVal) {
      console.log("list month", this.selectedChatGptActivityLog.length);
      this.modelData = this.findModelData(this.selectedMonth);
    },
    selectedAiMonth(newVal) {
      if (!newVal) return;
      const companyId = this.$route.params.companyId;
      this.$store.dispatch("companies/getDateListAiUsageLog", {
        companyId,
        newVal,
      });
    },
    // Show skeleton only if initial load takes longer than 200ms — avoids flash on fast cache hits.
    isInitialLoading: {
      immediate: true,
      handler(loading) {
        if (loading) {
          this.skeletonTimer = setTimeout(() => {
            this.showSkeletonAfterDelay = true;
          }, 200);
        } else {
          if (this.skeletonTimer) {
            clearTimeout(this.skeletonTimer);
            this.skeletonTimer = null;
          }
          this.showSkeletonAfterDelay = false;
        }
      },
    },
  },
  beforeDestroy() {
    if (this.skeletonTimer) clearTimeout(this.skeletonTimer);
  },
  mounted() {
    this.init();
    this.getMasterDataList();
  },
  methods: {
    fetchDateData() {
      let companyId = this.$route.params.companyId;
      this.$store.dispatch("companies/getDateListChatGptActivityLog", {
        companyId,
        newVal: this.selectedMonth,
      });
    },
    findModelData(month) {
      const selectedData = this.selectedChatGptActivityLog.find(
        (item) => item.id === this.selectedMonth
      );
      return selectedData ? selectedData.modelData : [];
    },
    getCurrentMonth() {
      const now = new Date();
      const year = now.getFullYear();
      const month = now.getMonth() + 1;
      const formattedMonth = month < 10 ? `0${month}` : `${month}`;
      return `${year}-${formattedMonth}`;
    },
    getChatGptActivityLog() {
      console.log("start", this.selectedChatGptActivityLog);
      let companyId = this.$route.params.companyId;
      this.$store.dispatch("companies/getChatGptActivityLog", { companyId });
    },
    getAiUsageLog() {
      const companyId = this.$route.params.companyId;
      this.$store.dispatch("companies/getAiUsageLog", { companyId });
    },
    toEntries(map, keyName) {
      if (!map || typeof map !== "object") return [];
      return Object.keys(map).map((k) => {
        const v = map[k] || {};
        return {
          [keyName]: k,
          countDetails: v.countDetails || 0,
          tokenPrompt: v.tokenPrompt || 0,
          tokenCompletion: v.tokenCompletion || 0,
          tokenTotal: v.tokenTotal || 0,
          cachedTokens: v.cachedTokens || 0,
          reasoningTokens: v.reasoningTokens || 0,
        };
      });
    },
    formatAiNumbers(data) {
      if (!Array.isArray(data)) return [];
      return data.map((item) => ({
        ...item,
        countDetails: (item.countDetails || 0).toLocaleString(),
        tokenPrompt: (item.tokenPrompt || 0).toLocaleString(),
        tokenCompletion: (item.tokenCompletion || 0).toLocaleString(),
        tokenTotal: (item.tokenTotal || 0).toLocaleString(),
        cachedTokens: (item.cachedTokens || 0).toLocaleString(),
        reasoningTokens: (item.reasoningTokens || 0).toLocaleString(),
      }));
    },
    rowClass() {
      return "row_Class";
    },
    formatNumbers(data) {
      return data.map((item) => ({
        ...item,
        countDetails: item.countDetails.toLocaleString(),
        tokenPrompt: item.tokenPrompt.toLocaleString(),
        tokenCompletion: item.tokenCompletion.toLocaleString(),
        tokenTotal: item.tokenTotal.toLocaleString(),
      }))},
    showHistoryDialog() {
      this.isShowHistoryDialog = true;
    },
    // Handle Element UI table selection change
    handleSelectionChange(selection) {
      this.selectedVendorIds = selection.map(item => item.vendorId);
    },
    // Preprocess for venSearchString (computed on save, not exported to CSV)
    preprocess(text) {
      return text
        .toLowerCase()
        .replace(/[^\w\s\u0E00-\u0E7F]/g, "")
        .replace(/\s+/g, " ")
        .trim();
    },
    // ── API Key & Connection Methods ──
    toggleShowKey() {
      this.showKey = !this.showKey;
    },
    async testConnection() {
      this.isTestingConnection = true;
      try {
        const response = await fetch("https://api.openai.com/v1/models", {
          headers: { Authorization: `Bearer ${this.apiKey}` },
        });
        if (response.ok) {
          this.notify("success", "การเชื่อมต่อสำเร็จ");
        } else {
          throw new Error("Connection failed");
        }
      } catch (error) {
        this.notify("error", "การเชื่อมต่อล้มเหลว กรุณาตรวจสอบ API Key");
      } finally {
        this.isTestingConnection = false;
      }
    },
    async saveApiKey() {
      this.isSavingApiKey = true;
      try {
        await this.$store.dispatch("companies/updateApiKey", {
          companyId: this.$route.params.companyId,
          apiKey: this.apiKey,
        });
        this.notify("success", "บันทึก API Key สำเร็จ");
      } catch (error) {
        this.notify("error", "เกิดข้อผิดพลาดในการบันทึก API Key");
      } finally {
        this.isSavingApiKey = false;
      }
    },

    // ── Vendor Methods ──
    confirmVendorDelete(vendor) {
      this.vendorToDelete = vendor;
      this.showVendorDeleteDialog = true;
    },
    async deleteVendor() {
      if (this.vendorToDelete) {
        const vendorId = this.vendorToDelete.vendorId;
        // Track the vendor ID for deletion
        this.vendorsToDelete.push(vendorId);
        // Remove from the local editedVendors array
        this.editedVendors = this.editedVendors.filter((vendor) => vendor.vendorId !== vendorId);
        // Also remove from bulk selection if selected
        this.selectedVendorIds = this.selectedVendorIds.filter((id) => id !== vendorId);
        this.hasUnsavedChanges = true;
        this.notify("success", "ลบข้อมูลในหน้าจอเรียบร้อย (ยังไม่บันทึก)");
        this.vendorToDelete = null;
        this.showVendorDeleteDialog = false;
      }
    },
    editVendor(vendor) {
      this.isEditMode = true;
      // Open the vendor form dialog with a deep copy.
      this.apVendor = JSON.parse(JSON.stringify(vendor));
      this.showApVendorFormDialog = true;
    },
    addNewVendor() {
      this.isEditMode = false;
      this.apVendor = {};
      this.showApVendorFormDialog = true;
    },
    // Called when the vendor form dialog emits "vendor-updated"
    updateVendor(updatedVendor) {
      const idx = this.editedVendors.findIndex((v) => v.vendorId === updatedVendor.vendorId);
      if (idx !== -1) {
        this.$set(this.editedVendors, idx, updatedVendor);
      } else {
        this.editedVendors.push(updatedVendor);
      }
      this.hasUnsavedChanges = true;
      this.notify("success", "แก้ไขข้อมูลในหน้าจอแล้ว (ยังไม่บันทึก)");
    },
    async getMasterDataList() {
      const masterRef = await fc.companies.doc(this.companyID).collection("PurchaseInvoiceMaster").get();
      const masterList = masterRef.docs.map((doc) => ({ ...doc.data(), docId: doc.id }));
      this.expenseList = masterList?.find((item) => item.docId === "Expense")?.data || [];
      this.vatList = masterList?.find((item) => item.docId === "Vat")?.data || [];
      this.whtList = masterList?.find((item) => item.docId === "WithholdingTax")?.data || [];
      this.paymentList = masterList?.find((item) => item.docId === "PaymentAccount")?.data || [];
    },
    notify(type, message) {
      this.snackbar = { show: true, message, color: type };
    },
    async init() {
      this.isInitializing = true;
      this.companyID = this.$route.params.companyId;
      if (this.companyID) {
        try {
          await Promise.all([
            this.$store.dispatch("user/getRoleUser", { companyId: this.companyID }),
            this.$store.dispatch("companies/fetchApVendors", { companyId: this.companyID }),
            this.$store.dispatch("companies/getCompanyProductList", { companyId: this.companyID }),
          ]);
          // Deep copy vendors from store to local editedVendors.
          this.editedVendors = JSON.parse(JSON.stringify(this.selectedCompanyApVendors));
        } catch (error) {
          console.error("Error initializing:", error);
          this.notify("error", "เกิดข้อผิดพลาดในการดึงข้อมูล");
        }
      }
      this.isInitializing = false;
      // Fire-and-forget — secondary metadata, must not block the table.
      this.loadFoxproLastSync();
    },
    async loadFoxproLastSync() {
      try {
        const companyId = this.$route.params.companyId;
        if (!companyId) return;
        const doc = await fc.companies.doc(companyId).collection("FoxproConfig").doc("config").get();
        if (doc.exists) {
          const data = doc.data() || {};
          this.foxproLastSyncedAt = data.lastSyncedAt || null;
          this.foxproLastSyncedBy = data.lastSyncedBy || "";
        }
      } catch (error) {
        console.error("Error loading FoxPro last sync:", error);
      }
    },
    async saveChanges() {
      // When the user clicks "บันทึกข้อมูลผู้จำหน่าย", commit local vendor changes and  selections to Firebase.
      this.savingChanges = true;
      try {
        const companyId = this.$route.params.companyId;
        const vendorCollectionRef = fc.companies.doc(companyId).collection("PurchaseInvoiceVendors");
        const batch = fc.db.batch();

        // Delete vendors that were removed
        this.vendorsToDelete.forEach((vendorId) => {
          const docRef = vendorCollectionRef.doc(vendorId);
          batch.delete(docRef);
        });

        // Update or add vendors from the local editedVendors array.
        // Document ID is always venCode (vendor code = SUPCOD from FoxPro).
        const missingCode = this.editedVendors.filter((v) => !v.venCode || !String(v.venCode).trim());
        if (missingCode.length > 0) {
          this.notify("error", `ไม่สามารถบันทึกได้: มีผู้จำหน่าย ${missingCode.length} รายการที่ไม่มีรหัสบริษัท (venCode)`);
          this.savingChanges = false;
          return;
        }

        const now = firebase.firestore.FieldValue.serverTimestamp();
        this.editedVendors.forEach((vendor) => {
          vendor.venSearchString = this.preprocess(
            `${vendor.venName} ${vendor.venBranch === "00000" ? vendor.venBranch : ""} ${vendor.venTaxId}`
          );
          const docId = String(vendor.venCode).trim();
          const oldVendorId = vendor.vendorId;
          // If this row was previously stored under an auto-ID, drop the old doc.
          if (oldVendorId && oldVendorId !== docId) {
            batch.delete(vendorCollectionRef.doc(oldVendorId));
          }
          vendor.vendorId = docId;
          if (!vendor.createdAt) vendor.createdAt = now;
          vendor.updatedAt = now;
          batch.set(vendorCollectionRef.doc(docId), vendor, { merge: true });
        });


        await batch.commit();
        // Clear the deletion tracker after successful commit
        this.vendorsToDelete = [];
        this.hasUnsavedChanges = false;
        this.notify("success", "บันทึกข้อมูลผู้จำหน่ายสำเร็จ");
        await this.$store.dispatch("companies/fetchApVendors", { companyId });
      } catch (error) {
        console.error("Error saving vendors", error);
        this.notify("error", "เกิดข้อผิดพลาดในการบันทึกข้อมูลผู้จำหน่าย");
      } finally {
        this.savingChanges = false;
      }
    },
    // ── Bulk Delete Methods ──
    toggleSelectAll() {
      const filteredIds = this.filteredVendors.map((v) => v.vendorId);
      if (this.selectedVendorIds.length === filteredIds.length) {
        this.selectedVendorIds = [];
      } else {
        this.selectedVendorIds = filteredIds;
      }
    },
    confirmBulkDelete() {
      this.showBulkDeleteDialog = true;
    },
    confirmSyncVendors() {
      if (this.hasUnsavedChanges) {
        this.notify("error", "กรุณาบันทึกการเปลี่ยนแปลงก่อนเริ่ม Sync");
        return;
      }
      this.showSyncVendorsDialog = true;
    },
    async syncVendorsFromFoxpro() {
      try {
        this.isSyncingVendors = true;
        const companyId = this.$route.params.companyId;

        const cloudFunctionUrl = `${fc.cloudFunctionUrl}apiFoxproSyncVendors`;

        const response = await axios.post(
          cloudFunctionUrl,
          { companyId },
          { headers: { "Content-Type": "application/json" } }
        );

        if (!response.data || !response.data.success) {
          throw new Error((response.data && response.data.error) || "Sync failed");
        }

        // Record last successful sync into FoxproConfig for display in the confirm dialog.
        const currentUser = firebase.auth && firebase.auth().currentUser;
        const userEmail = (currentUser && currentUser.email) || "";
        try {
          await fc.companies.doc(companyId).collection("FoxproConfig").doc("config").set({
            lastSyncedAt: firebase.firestore.FieldValue.serverTimestamp(),
            lastSyncedBy: userEmail,
          }, { merge: true });
          this.foxproLastSyncedAt = new Date();
          this.foxproLastSyncedBy = userEmail;
        } catch (logErr) {
          // Logging failure must not break the sync flow.
          console.error("Failed to record FoxPro sync log:", logErr);
        }

        const r = response.data.data || {};
        this.syncVendorsResult = r;
        const migratedNote = r.migrated ? `, ย้าย docID ${r.migrated}` : "";
        this.notify(
          "success",
          `Sync สำเร็จ — เพิ่ม ${r.added || 0}, อัปเดต ${r.updated || 0}, ข้าม ${r.skipped || 0}${migratedNote} (รวม ${r.total || 0})`
        );

        // Reload from store; watcher refreshes local editedVendors when no unsaved changes
        await this.$store.dispatch("companies/fetchApVendors", { companyId });
        this.editedVendors = JSON.parse(JSON.stringify(this.selectedCompanyApVendors));
      } catch (error) {
        console.error("FoxPro sync vendors error:", error);
        let msg = "";
        if (error.response && error.response.data) {
          const d = error.response.data;
          msg = d.error || d.message || `HTTP ${error.response.status}`;
        } else if (error.code === "ERR_NETWORK" || (error.message || "").includes("Network Error")) {
          msg = "ไม่สามารถเชื่อมต่อ Cloud Function ได้";
        } else {
          msg = error.message || "Sync ล้มเหลว";
        }
        this.notify("error", `Sync ล้มเหลว: ${msg}`);
      } finally {
        this.isSyncingVendors = false;
        this.showSyncVendorsDialog = false;
      }
    },
    bulkDelete() {
      // Add all selected vendor IDs to the deletion tracker
      this.selectedVendorIds.forEach((id) => {
        if (!this.vendorsToDelete.includes(id)) {
          this.vendorsToDelete.push(id);
        }
      });
      // Remove the selected vendors from the local list
      this.editedVendors = this.editedVendors.filter(
        (vendor) => !this.selectedVendorIds.includes(vendor.vendorId)
      );
      // Clear bulk selection and close the dialog
      this.selectedVendorIds = [];
      this.showBulkDeleteDialog = false;
      this.hasUnsavedChanges = true;
      this.notify("success", "ลบผู้จำหน่ายที่เลือกในหน้าจอเรียบร้อย (ยังไม่บันทึก)");
    },

    // ── CSV Template Download & Upload (using PapaParse) ──
    flattenVendor(vendor) {
      return {
        vendorId: vendor.vendorId || "",
        venCode: vendor.venCode || "",
        venPrefix: vendor.venPrefix || "",
        venName: vendor.venName || "",
        venBranch: vendor.venBranch || "",
        venAddr: vendor.venAddr || "",
        venTaxId: vendor.venTaxId || "",
        venZipCode: vendor.venZipCode || "",
        payCode: vendor.payCode || "",
        payData_payCode: vendor.payData?.payCode || "",
        payData_payName: vendor.payData?.payName || "",
        payData_tranType: vendor.payData?.tranType || "",
        isRejected: vendor.isRejected === 'TRUE' ? true : false,
        isIncludeVat: vendor.isIncludeVat === 'TRUE' ? true : false,
        isForeign: vendor.isForeign === 'TRUE' ? true : false,
        defaultAcctP00_expCode: vendor.defaultAcctP00?.expCode || "",
        defaultAcctP00_expData_expName: vendor.defaultAcctP00?.expData?.expName || "",
        defaultAcctP00_expData_acctCode: vendor.defaultAcctP00?.expData?.acctCode || "",
        defaultAcctP00_expData_acctName: vendor.defaultAcctP00?.expData?.acctName || "",
        defaultAcctP00_expData_tranType: vendor.defaultAcctP00?.expData?.tranType || "",
        defaultAcctP00_vatCode: vendor.defaultAcctP00?.vatCode || "",
        defaultAcctP00_vatData_vatName: vendor.defaultAcctP00?.vatData?.vatName || "",
        defaultAcctP00_vatData_vatType: vendor.defaultAcctP00?.vatData?.vatType || "",
        defaultAcctP00_vatData_acctCode: vendor.defaultAcctP00?.vatData?.acctCode || "",
        defaultAcctP00_vatData_acctName: vendor.defaultAcctP00?.vatData?.acctName || "",
        defaultAcctP00_vatData_tranType: vendor.defaultAcctP00?.vatData?.tranType || "",
        defaultAcctP00_whtCode: vendor.defaultAcctP00?.whtCode || "",
        defaultAcctP00_whtData_whtName: vendor.defaultAcctP00?.whtData?.whtName || "",
        defaultAcctP00_whtData_whtRate: vendor.defaultAcctP00?.whtData?.whtRate || "",
        defaultAcctP00_whtData_acctCode: vendor.defaultAcctP00?.whtData?.acctCode || "",
        defaultAcctP00_whtData_acctName: vendor.defaultAcctP00?.whtData?.acctName || "",
        defaultAcctP00_whtData_tranType: vendor.defaultAcctP00?.whtData?.tranType || "",
        defaultAcctP07_expCode: vendor.defaultAcctP07?.expCode || "",
        defaultAcctP07_expData_expName: vendor.defaultAcctP07?.expData?.expName || "",
        defaultAcctP07_expData_acctCode: vendor.defaultAcctP07?.expData?.acctCode || "",
        defaultAcctP07_expData_acctName: vendor.defaultAcctP07?.expData?.acctName || "",
        defaultAcctP07_expData_tranType: vendor.defaultAcctP07?.expData?.tranType || "",
        defaultAcctP07_vatCode: vendor.defaultAcctP07?.vatCode || "",
        defaultAcctP07_vatData_vatName: vendor.defaultAcctP07?.vatData?.vatName || "",
        defaultAcctP07_vatData_vatType: vendor.defaultAcctP07?.vatData?.vatType || "",
        defaultAcctP07_vatData_acctCode: vendor.defaultAcctP07?.vatData?.acctCode || "",
        defaultAcctP07_vatData_acctName: vendor.defaultAcctP07?.vatData?.acctName || "",
        defaultAcctP07_vatData_tranType: vendor.defaultAcctP07?.vatData?.tranType || "",
        defaultAcctP07_whtCode: vendor.defaultAcctP07?.whtCode || "",
        defaultAcctP07_whtData_whtName: vendor.defaultAcctP07?.whtData?.whtName || "",
        defaultAcctP07_whtData_whtRate: vendor.defaultAcctP07?.whtData?.whtRate || "",
        defaultAcctP07_whtData_acctCode: vendor.defaultAcctP07?.whtData?.acctCode || "",
        defaultAcctP07_whtData_acctName: vendor.defaultAcctP07?.whtData?.acctName || "",
        defaultAcctP07_whtData_tranType: vendor.defaultAcctP07?.whtData?.tranType || "",
      };
    },
    unflattenVendor(row) {
      const vendor = {
        vendorId: row.vendorId,
        venCode: row.venCode,
        venPrefix: row.venPrefix,
        venName: row.venName,
        venBranch: row.venBranch,
        venAddr: row.venAddr,
        venTaxId: row.venTaxId,
        venZipCode: row.venZipCode,
        payCode: row.payCode,
        payData: {
          payCode: row.payData_payCode,
          payName: row.payData_payName,
          tranType: row.payData_tranType,
        },
        isRejected: row.isRejected === 'TRUE' ? true : false,
        isIncludeVat: row.isIncludeVat === 'TRUE' ? true : false,
        isForeign: row.isForeign === 'TRUE' ? true : false,
        defaultAcctP00: {
          expCode: row.defaultAcctP00_expCode,
          expData: {
            expName: row.defaultAcctP00_expData_expName,
            acctCode: row.defaultAcctP00_expData_acctCode,
            acctName: row.defaultAcctP00_expData_acctName,
            tranType: row.defaultAcctP00_expData_tranType,
          },
          vatCode: row.defaultAcctP00_vatCode,
          vatData: {
            vatName: row.defaultAcctP00_vatData_vatName,
            vatType: row.defaultAcctP00_vatData_vatType,
            acctCode: row.defaultAcctP00_vatData_acctCode,
            acctName: row.defaultAcctP00_vatData_acctName,
            tranType: row.defaultAcctP00_vatData_tranType,
          },
          whtCode: row.defaultAcctP00_whtCode,
          whtData: {
            whtName: row.defaultAcctP00_whtData_whtName,
            whtRate: row.defaultAcctP00_whtData_whtRate,
            acctCode: row.defaultAcctP00_whtData_acctCode,
            acctName: row.defaultAcctP00_whtData_acctName,
            tranType: row.defaultAcctP00_whtData_tranType,
          },
        },
        defaultAcctP07: {
          expCode: row.defaultAcctP07_expCode,
          expData: {
            expName: row.defaultAcctP07_expData_expName,
            acctCode: row.defaultAcctP07_expData_acctCode,
            acctName: row.defaultAcctP07_expData_acctName,
            tranType: row.defaultAcctP07_expData_tranType,
          },
          vatCode: row.defaultAcctP07_vatCode,
          vatData: {
            vatName: row.defaultAcctP07_vatData_vatName,
            vatType: row.defaultAcctP07_vatData_vatType,
            acctCode: row.defaultAcctP07_vatData_acctCode,
            acctName: row.defaultAcctP07_vatData_acctName,
            tranType: row.defaultAcctP07_vatData_tranType,
          },
          whtCode: row.defaultAcctP07_whtCode,
          whtData: {
            whtName: row.defaultAcctP07_whtData_whtName,
            whtRate: row.defaultAcctP07_whtData_whtRate,
            acctCode: row.defaultAcctP07_whtData_acctCode,
            acctName: row.defaultAcctP07_whtData_acctName,
            tranType: row.defaultAcctP07_whtData_tranType,
          },
        },
      };

      return vendor;
    },
    openUpdateExpenseDialog() {
      this.updateExpenseFile = null;
      this.updateExpenseRows = [];
      this.showUpdateExpenseDialog = true;
    },
    closeUpdateExpenseDialog() {
      this.showUpdateExpenseDialog = false;
      this.updateExpenseFile = null;
      this.updateExpenseRows = [];
    },
    downloadUpdateExpenseTemplate() {
      // Blank template — header row only, user fills in the values.
      const headers = ["VendorCode", "Expense Code P07", "Expense Code P00", "whtcode"];
      const csvContent = Papa.unparse({ fields: headers, data: [] });
      const bom = "﻿";
      const blob = new Blob([bom + csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "update_expense_template.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    onUpdateExpenseFileChange() {
      // Picking a new file invalidates the previous preview.
      this.updateExpenseRows = [];
    },
    previewUpdateExpenseFile() {
      if (!this.updateExpenseFile) return;
      const name = String(this.updateExpenseFile.name || "").toLowerCase();
      if (!name.endsWith(".csv")) {
        this.notify("error", "ไฟล์ต้องเป็นนามสกุล .csv");
        return;
      }
      if (!name.includes("update_expense_template")) {
        this.notify(
          "error",
          "ชื่อไฟล์ต้องมีคำว่า 'update_expense_template' (กรุณาใช้ template ที่ดาวน์โหลดจากปุ่มด้านบน)"
        );
        return;
      }
      this.isUploadingUpdateExpense = true;
      this.updateExpenseRows = [];
      const requiredHeaders = ["VendorCode", "Expense Code P07", "Expense Code P00", "whtcode"];
      Papa.parse(this.updateExpenseFile, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          const fields = (results.meta && results.meta.fields) || [];
          const missing = requiredHeaders.filter((h) => !fields.includes(h));
          if (missing.length) {
            this.notify("error", `คอลัมน์ไม่ครบ: ขาด ${missing.join(", ")}`);
            this.isUploadingUpdateExpense = false;
            return;
          }
          const rows = results.data || [];
          if (!rows.length) {
            this.notify("error", "ไม่มีข้อมูลในไฟล์ CSV");
            this.isUploadingUpdateExpense = false;
            return;
          }
          this.updateExpenseRows = rows.map((row) => ({
            VendorCode: String(row["VendorCode"] || "").trim(),
            "Expense Code P07": String(row["Expense Code P07"] || "").trim(),
            "Expense Code P00": String(row["Expense Code P00"] || "").trim(),
            whtcode: String(row["whtcode"] || "").trim(),
          }));
          this.notify(
            "success",
            `อ่านข้อมูล ${this.updateExpenseRows.length} แถว — กรุณาตรวจสอบและกดยืนยัน`
          );
          this.isUploadingUpdateExpense = false;
        },
        error: (err) => {
          console.error("Error parsing CSV", err);
          this.notify("error", "เกิดข้อผิดพลาดในการอ่านไฟล์ CSV");
          this.isUploadingUpdateExpense = false;
        },
      });
    },
    applyUpdateExpense() {
      if (!this.updateExpenseRows.length) return;
      this.isApplyingUpdateExpense = true;
      try {
        // Apply per text.txt diagram:
        //   match vendor by venCode
        //   defaultAcctP00.expCode = "Expense Code P00"; expData looked up from expenseList
        //   defaultAcctP07.expCode = "Expense Code P07"; expData looked up from expenseList
        //   defaultAcctP00.whtCode = defaultAcctP07.whtCode = "whtcode"; whtData looked up from whtList
        let updated = 0;
        let unmatchedVendors = 0;
        let unmatchedCodes = 0;
        this.updateExpenseRows.forEach((row) => {
          const venCode = row.VendorCode;
          if (!venCode) return;
          const idx = this.editedVendors.findIndex((v) => v.venCode === venCode);
          if (idx === -1) { unmatchedVendors++; return; }

          const v = JSON.parse(JSON.stringify(this.editedVendors[idx]));
          if (!v.defaultAcctP00) v.defaultAcctP00 = {};
          if (!v.defaultAcctP07) v.defaultAcctP07 = {};

          const p00ExpCode = row["Expense Code P00"];
          const p07ExpCode = row["Expense Code P07"];
          const whtCode = row.whtcode;

          if (p00ExpCode) {
            v.defaultAcctP00.expCode = p00ExpCode;
            const exp = this.expenseList.find((e) => e.expCode === p00ExpCode);
            if (exp) {
              v.defaultAcctP00.expData = {
                acctCode: exp.acctCode || "",
                acctName: exp.acctName || "",
                expCode: exp.expCode || "",
                expName: exp.expName || "",
                tranType: exp.tranType || 0,
              };
            } else {
              unmatchedCodes++;
            }
          }
          if (p07ExpCode) {
            v.defaultAcctP07.expCode = p07ExpCode;
            const exp = this.expenseList.find((e) => e.expCode === p07ExpCode);
            if (exp) {
              v.defaultAcctP07.expData = {
                acctCode: exp.acctCode || "",
                acctName: exp.acctName || "",
                expCode: exp.expCode || "",
                expName: exp.expName || "",
                tranType: exp.tranType || 0,
              };
            } else {
              unmatchedCodes++;
            }
          }
          if (whtCode) {
            v.defaultAcctP00.whtCode = whtCode;
            v.defaultAcctP07.whtCode = whtCode;
            const wht = this.whtList.find((w) => w.whtCode === whtCode);
            if (wht) {
              const whtData = {
                acctCode: wht.acctCode || "",
                acctName: wht.acctName || "",
                tranType: wht.tranType || 0,
                whtCode: wht.whtCode || "",
                whtName: wht.whtName || "",
                whtRate: wht.whtRate || "",
              };
              v.defaultAcctP00.whtData = whtData;
              v.defaultAcctP07.whtData = { ...whtData };
            } else {
              unmatchedCodes++;
            }
          }

          this.$set(this.editedVendors, idx, v);
          updated++;
        });

        if (updated > 0) this.hasUnsavedChanges = true;
        const parts = [`อัปเดต ${updated} รายการ`];
        if (unmatchedVendors) parts.push(`ไม่พบ vendor ${unmatchedVendors}`);
        if (unmatchedCodes) parts.push(`รหัสที่ไม่พบ ${unmatchedCodes}`);
        this.notify(
          updated > 0 ? "success" : "error",
          parts.join(", ") + " (ยังไม่บันทึก)"
        );
        this.closeUpdateExpenseDialog();
      } catch (err) {
        console.error("Error applying expense update:", err);
        this.notify("error", "เกิดข้อผิดพลาดในการอัปเดต");
      } finally {
        this.isApplyingUpdateExpense = false;
      }
    },
    openCsvTemplateDialog() {
      this.selectedFile = null;
      this.showCsvTemplateDialog = true;
    },
    closeCsvTemplateDialog() {
      this.showCsvTemplateDialog = false;
      this.selectedFile = null;
    },
    downloadVendorTemplate() {
      const headers = Object.keys(this.flattenVendor({}));
      const flatData = this.editedVendors.map((vendor) => this.flattenVendor(vendor));
      const csvContent = Papa.unparse(flatData, { fields: headers });
      const bom = "\ufeff";
      const blob = new Blob([bom + csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "vendor_config_template.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    confirmUpload() {
      if (!this.selectedFile) return;
      this.uploadLoading = true;
      Papa.parse(this.selectedFile, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          const data = results.data;
          if (!data.length) {
            this.notify("error", "ไม่มีข้อมูลผู้จำหน่ายที่สามารถอัปโหลดได้");
            this.uploadLoading = false;
            return;
          }
          const csvVendors = data.map((row) => this.unflattenVendor(row));
          csvVendors.forEach((csvVendor) => {
            const index = this.editedVendors.findIndex((vendor) => vendor.venCode === csvVendor.venCode);
            if (index !== -1) {
              this.$set(this.editedVendors, index, { ...this.editedVendors[index], ...csvVendor });
            } else {
              this.editedVendors.push(csvVendor);
            }
          });
          this.hasUnsavedChanges = true;
          this.notify("success", "อัปโหลด CSV เสร็จสิ้น (ยังไม่บันทึก)");
          this.closeCsvTemplateDialog();
          this.uploadLoading = false;
        },
        error: (err) => {
          console.error("Error parsing CSV", err);
          this.notify("error", "เกิดข้อผิดพลาดในการอ่านไฟล์ CSV");
          this.uploadLoading = false;
        },
      });
    },
  }
};
</script>

<style lang="scss" scoped>
.v-tab {
  text-transform: none;
  padding-right: 8px !important;
  .v-btn {
    margin-left: 8px;
  }
}

.row-wrapper {
  padding-left: 20px;
}

.nowrap th,
td {
  white-space: nowrap;
}

.expanded-item-td {
  padding: 0;
}

.v-data-table
  > .v-data-table__wrapper
  tbody
  tr.v-data-table__expanded__content {
  box-shadow: 0 0 0 0 !important;
}

.my-header-style {
  background: rgba(59, 130, 246, 0.5);
}

.row_Class {
  background-color: rgba(0, 0, 0, 0.12);
}

.vendor-avatar {
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.img-show {
  width: 6rem;
  height: 6rem;
  object-fit: contain;
}

.items-per-page-selector {
  padding: 0.25rem 0.5rem;
  border-radius: 0.5rem;
  border: 1px solid #ccc;
  background-color: #ffffff;
  text-align: center;
  /* Match the count text on the left */
  font-family: inherit;
  font-size: inherit;
  font-weight: inherit;
  color: inherit;
  line-height: inherit;
}

.vendor-skeleton {
  width: 100%;
  overflow-x: auto;
}

.vendor-skeleton__inner {
  // Sum of fixed column widths in the real table; keeps skeleton aligned with horizontal scroll.
  min-width: 1660px;
}

.vendor-skeleton__header,
.vendor-skeleton__row {
  display: flex;
  align-items: center;
  border-bottom: 1px solid #ebeef5;
}

.vendor-skeleton__header {
  height: 40px;
  background: #fafafa;
  font-weight: 600;
  color: #909399;
  font-size: 13px;
}

.vendor-skeleton__row {
  height: 48px;
}

.vendor-skeleton__cell {
  flex: 0 0 auto;
  padding: 0 10px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.vendor-skeleton__cell--flex {
  flex: 1 1 auto;
  min-width: 300px;
}

.skel-bar {
  height: 14px;
  border-radius: 4px;
  background: linear-gradient(
    90deg,
    #ebeef5 0%,
    #f5f7fa 50%,
    #ebeef5 100%
  );
  background-size: 200% 100%;
  animation: vendor-skeleton-shimmer 1.4s ease-in-out infinite;
}

.skel-bar--checkbox {
  width: 16px;
  height: 16px;
  border-radius: 2px;
}

.skel-bar--circle {
  width: 28px;
  height: 28px;
  border-radius: 50%;
}

@keyframes vendor-skeleton-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

@media (prefers-reduced-motion: reduce) {
  .skel-bar {
    animation: none;
    background: #ebeef5;
  }
}
</style>
